/**
 * Historical Stock API service
 * Handles all historical stock-related API operations
 */

import { httpClient } from '@/utils/httpClient';
import type {
    APIHistoricalStock,
    APIHistoricalStockRequest,
    DRFPaginatedResponse,
    HistoricalChangeType,
} from '@/types';

const buildQuery = (params: Record<string, unknown>): string => {
    const usp = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null) usp.append(k, String(v));
    });
    const s = usp.toString();
    return s ? `?${s}` : '';
};

export interface HistoricalStockListParams {
    page?: number;
    page_size?: number;
    change_type?: HistoricalChangeType;
    material?: number;
    user?: number;
}

const getMovementSuccessMessage = (changeType: HistoricalChangeType): string => {
    switch (changeType) {
        case 'addition':
            return 'Se registró una entrada de stock.';
        case 'removal':
            return 'Se registró una salida de stock.';
        default:
            return 'Se registró un ajuste de stock.';
    }
};

export const historicalStockService = {
    async list(params?: HistoricalStockListParams): Promise<DRFPaginatedResponse<APIHistoricalStock>> {
        const qs = buildQuery((params ?? {}) as Record<string, unknown>);
        return httpClient.get<DRFPaginatedResponse<APIHistoricalStock>>(`/historical-stock/${qs}`);
    },

    async getById(id: number): Promise<APIHistoricalStock> {
        return httpClient.get<APIHistoricalStock>(`/historical-stock/${id}/`);
    },

    async create(data: APIHistoricalStockRequest): Promise<APIHistoricalStock> {
        return httpClient.post<APIHistoricalStock, APIHistoricalStockRequest>('/historical-stock/', data, {
            successTitle: 'Movimiento registrado',
            successMessage: getMovementSuccessMessage(data.change_type),
            errorTitle: 'Error en movimiento de stock',
        });
    },

    async update(id: number, data: APIHistoricalStockRequest): Promise<APIHistoricalStock> {
        return httpClient.put<APIHistoricalStock, APIHistoricalStockRequest>(`/historical-stock/${id}/`, data, {
            successTitle: 'Movimiento actualizado',
            successMessage: 'Se actualizaron los datos del movimiento de stock.',
            errorTitle: 'Error en movimiento de stock',
        });
    },

    async patch(id: number, data: Partial<APIHistoricalStockRequest>): Promise<APIHistoricalStock> {
        return httpClient.patch<APIHistoricalStock, Partial<APIHistoricalStockRequest>>(
            `/historical-stock/${id}/`,
            data,
            {
                successTitle: 'Movimiento actualizado',
                successMessage: 'Se aplicaron cambios al movimiento de stock.',
                errorTitle: 'Error en movimiento de stock',
            }
        );
    },

    async remove(id: number): Promise<void> {
        return httpClient.delete<void>(`/historical-stock/${id}/`, {
            successTitle: 'Movimiento eliminado',
            successMessage: 'El movimiento de stock se eliminó correctamente.',
            errorTitle: 'Error en movimiento de stock',
        });
    },
};

export default historicalStockService;
